import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { m } from 'motion/react';
import { useAuth } from '@/auth/AuthProvider';
import { Sheet } from '@/design/components/Dialog';
import { toast } from '@/design/components/toast';
import { getSession } from '@/auth/session';
import { pageTransition } from '@/design/motion/presets';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { MobileTabBar } from './MobileTabBar';
import { ReauthDialog } from './ReauthDialog';
import { Sidebar, useVisibleNav } from './Sidebar';
import { Topbar } from './Topbar';
import { useCapabilities } from '@/app/capabilities';
import { HelpProvider } from '@/app/help/HelpProvider';

export function RequireAuth() {
  const { session } = useAuth();
  const location = useLocation();
  if (!session)
    return (
      <Navigate to={`/ingresar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  return <AppShell />;
}

function AppShell() {
  const palette = useCommandPalette();
  const [more, setMore] = useState(false);
  const location = useLocation();
  useSessionExpiryWarning();
  const capabilities = useCapabilities();

  return (
    <HelpProvider>
      <div className="flex min-h-dvh">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar onSearch={() => palette.setOpen(true)} />
          {capabilities.operationsWritable === false && (
            <div
              role="status"
              className="border-b border-gold-300 bg-gold-50 px-4 py-3 text-sm text-ink-800 sm:px-6 lg:px-10"
            >
              <strong>Modo de protección: guardado temporalmente suspendido.</strong> Podés consultar la
              información. Conservá lo que estás escribiendo; podrás guardarlo cuando se restablezca la
              protección de los datos.
            </div>
          )}
          <main className="flex-1 px-4 pt-6 pb-28 sm:px-6 lg:px-10 lg:pb-12">
            {/* Enter-only transition: an exit animation would show the next page's content fading out. */}
            <m.div key={pageKey(location.pathname)} variants={pageTransition} initial="hidden" animate="show">
              <Outlet />
            </m.div>
          </main>
        </div>
        <MobileTabBar onMore={() => setMore(true)} />
        <MoreSheet open={more} onOpenChange={setMore} />
        <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
        <ReauthDialog />
      </div>
    </HelpProvider>
  );
}

/** The quote editor keeps its state when /presupuestos/nuevo becomes /presupuestos/:id after saving. */
function pageKey(pathname: string): string {
  return pathname.startsWith('/presupuestos/') ? '/presupuestos/editor' : pathname;
}

function MoreSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { sections } = useVisibleNav();
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Todo el sistema">
      <div className="space-y-5">
        {sections.map((s, i) => (
          <div key={i}>
            {s.title && <div className="overline mb-2">{s.title}</div>}
            <div className="grid grid-cols-2 gap-2">
              {s.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => onOpenChange(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-line bg-surface-0 px-3 py-3 text-[13.5px] shadow-e1 active:scale-[0.98]"
                >
                  <item.icon className="size-[18px] text-gold-600" strokeWidth={1.75} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

/** Warns ten minutes before the 8-hour session ends, so nobody loses a half-typed quote by surprise. */
function useSessionExpiryWarning() {
  useEffect(() => {
    const session = getSession();
    if (!session) return;
    const warnAt = new Date(session.expiresAt).getTime() - 10 * 60_000 - Date.now();
    if (warnAt <= 0) return;
    const t = setTimeout(
      () =>
        toast.info(
          'Tu sesión vence en 10 minutos',
          'Guardá lo que estés haciendo. Si vence, te pedimos la contraseña sin perder nada.',
        ),
      warnAt,
    );
    return () => clearTimeout(t);
  }, []);
}
